import type { Temporal } from "./temporal";

import {
  parseInstant,
  parseLocalDateTime,
  parsePlainDateTime,
} from "./parsing";
import { clock } from "./temporal";

/** Проверка зоны кэшируется: `Intl.DateTimeFormat` дорог, а зон в приложении единицы. */
const namedTimezones = new Map<string, boolean>();

export function isNamedTimezone(value: unknown): value is string {
  if (typeof value !== "string" || !/^[A-Za-z][A-Za-z0-9_+\-/]*$/.test(value))
    return false;
  const cached = namedTimezones.get(value);
  if (cached !== undefined) return cached;
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    namedTimezones.set(value, true);
    return true;
  } catch (error) {
    if (error instanceof RangeError) {
      namedTimezones.set(value, false);
      return false;
    }
    throw error;
  }
}

export function inTimezone(
  value: string | Temporal.Instant,
  timezone: unknown,
): Temporal.ZonedDateTime | null {
  if (!isNamedTimezone(timezone)) return null;
  const instant = typeof value === "string" ? parseInstant(value) : value;
  return instant?.toZonedDateTimeISO(timezone) ?? null;
}

export function todayInTimezone(
  timezone: unknown,
  now: Temporal.Instant = clock.now(),
): Temporal.PlainDate | null {
  return inTimezone(now, timezone)?.toPlainDate() ?? null;
}

/** Первый реальный момент дня; целиком пропущенная дата не переносится на соседнюю. */
export function startOfDay(
  date: Temporal.PlainDate,
  timezone: unknown,
): Temporal.ZonedDateTime | null {
  if (!isNamedTimezone(timezone)) return null;
  const start = date.toZonedDateTime(timezone);
  return start.toPlainDate().equals(date) ? start : null;
}

/** Gap пуст; fold содержит два различных момента, упорядоченных по UTC. */
export function localTimeCandidates(
  date: string,
  time: string,
  timezone: unknown,
): Temporal.ZonedDateTime[] {
  const local = parseLocalDateTime(date, time);
  if (!local || !isNamedTimezone(timezone)) return [];
  const earlier = local.toZonedDateTime(timezone, {
    disambiguation: "earlier",
  });
  const later = local.toZonedDateTime(timezone, { disambiguation: "later" });
  return [earlier, later].filter(
    (candidate, index) =>
      candidate.toPlainDateTime().equals(local) &&
      (index === 0 || !candidate.equals(earlier)),
  );
}

export function resolveLocalTime(
  date: string,
  time: string,
  timezone: unknown,
  offsetMinutes?: number,
): Temporal.ZonedDateTime | null {
  const candidates = localTimeCandidates(date, time, timezone);
  if (offsetMinutes !== undefined)
    return (
      candidates.find(
        (candidate) =>
          candidate.offsetNanoseconds / 60_000_000_000 === offsetMinutes,
      ) ?? null
    );
  return candidates.length === 1 ? candidates[0]! : null;
}

/** Только однозначное местное время в явно заданной IANA-зоне. */
export function parseLocalInstant(
  value: string,
  timezone: unknown,
): Temporal.Instant | null {
  const local = parsePlainDateTime(value);
  if (!local || !isNamedTimezone(timezone)) return null;
  try {
    return local
      .toZonedDateTime(timezone, { disambiguation: "reject" })
      .toInstant();
  } catch (error) {
    if (error instanceof RangeError) return null;
    throw error;
  }
}
